import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck } from 'lucide-react';
import { trackLead } from '../utils/metaPixel';
// === Respect-the-reader configuration ===
const MIN_TIME_ON_PAGE_MS = 45000; // Must be on page 45s before any trigger
const MIN_SCROLL_DEPTH_PCT = 30; // Must have scrolled 30% (proves engagement)
const IDLE_TRIGGER_MS = 90000; // Idle trigger fires only after 90s of inactivity
const IDLE_MIN_SCROLL_PCT = 50; // ...AND only if scroll depth >= 50%
const MOBILE_SCROLL_UP_TRIGGER_PCT = 70; // Mobile rapid scroll-up needs depth >= 70%
const RAPID_SCROLL_UP_VELOCITY = -1.5; // px/ms, more conservative than before
const DESKTOP_MOUSE_LEAVE_DEBOUNCE_MS = 300; // Wait 300ms before firing (avoid accidents)
const COOLDOWN_DAYS = 7; // Don't re-show for 7 days after dismissal
const COOLDOWN_KEY = 'blinkblis_exit_cooldown_until';
const SESSION_KEY = 'blinkblis_exit_shown';
export function ExitIntentModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  // Refs so we don't trigger React re-renders on every scroll/timer tick
  const activeTimeMsRef = useRef(0);
  const lastTickRef = useRef(Date.now());
  const isVisibleRef = useRef(!document.hidden);
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const mouseLeaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollYRef = useRef(0);
  const lastScrollTimeRef = useRef(Date.now());
  const hasFiredRef = useRef(false);
  const getScrollDepthPct = (): number => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return 0;
    return window.scrollY / scrollable * 100;
  };
  const canFire = (): boolean => {
    if (hasFiredRef.current) return false;
    if (sessionStorage.getItem(SESSION_KEY)) return false;
    // 7-day cooldown
    const cooldownUntil = localStorage.getItem(COOLDOWN_KEY);
    if (cooldownUntil && Date.now() < parseInt(cooldownUntil, 10)) return false;
    // Must have spent enough ACTIVE time on page (tab visible)
    if (activeTimeMsRef.current < MIN_TIME_ON_PAGE_MS) return false;
    // Must have scrolled enough to prove they were reading
    if (getScrollDepthPct() < MIN_SCROLL_DEPTH_PCT) return false;
    // Don't fire if another modal is already open
    const otherModalOpen = document.querySelector('[role="dialog"], .fixed.inset-0.z-50');
    if (otherModalOpen && !otherModalOpen.contains(document.activeElement)) {
      // The lead modal uses z-50 too — if visible and not us, skip
      const allFixed = document.querySelectorAll('.fixed.inset-0.z-50');
      if (allFixed.length > 0 && !isOpen) return false;
    }
    return true;
  };
  const triggerModal = (reason: string) => {
    if (!canFire()) return;
    hasFiredRef.current = true;
    sessionStorage.setItem(SESSION_KEY, 'true');
    // Set 7-day cooldown
    const cooldownUntil = Date.now() + COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(COOLDOWN_KEY, String(cooldownUntil));
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.debug('[ExitIntent] fired:', reason, {
        activeTimeS: Math.round(activeTimeMsRef.current / 1000),
        scrollPct: Math.round(getScrollDepthPct())
      });
    }
    setIsOpen(true);
  };
  useEffect(() => {
    // === Track ACTIVE time on page (only when tab visible) ===
    const tickActiveTime = () => {
      const now = Date.now();
      if (isVisibleRef.current) {
        activeTimeMsRef.current += now - lastTickRef.current;
      }
      lastTickRef.current = now;
    };
    const activeTimeInterval = setInterval(tickActiveTime, 1000);
    const handleVisibilityChange = () => {
      tickActiveTime();
      isVisibleRef.current = !document.hidden;
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // === Idle trigger ===
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        if (getScrollDepthPct() >= IDLE_MIN_SCROLL_PCT) {
          triggerModal('idle');
        }
      }, IDLE_TRIGGER_MS);
    };
    // === Desktop: mouse-leave top of viewport (debounced) ===
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY >= 0) return;
      // Don't fire instantly — wait 300ms in case it was an accidental cursor wobble
      if (mouseLeaveTimerRef.current) clearTimeout(mouseLeaveTimerRef.current);
      mouseLeaveTimerRef.current = setTimeout(() => {
        triggerModal('mouse_leave_top');
      }, DESKTOP_MOUSE_LEAVE_DEBOUNCE_MS);
    };
    const handleMouseEnter = () => {
      // User came back — cancel pending trigger
      if (mouseLeaveTimerRef.current) {
        clearTimeout(mouseLeaveTimerRef.current);
        mouseLeaveTimerRef.current = null;
      }
    };
    // === Mobile: rapid scroll-up at high depth ===
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const currentTime = Date.now();
      const timeDiff = currentTime - lastScrollTimeRef.current;
      if (timeDiff > 0) {
        const velocity = (currentScrollY - lastScrollYRef.current) / timeDiff;
        if (velocity < RAPID_SCROLL_UP_VELOCITY && getScrollDepthPct() >= MOBILE_SCROLL_UP_TRIGGER_PCT) {
          triggerModal('rapid_scroll_up');
        }
      }
      lastScrollYRef.current = currentScrollY;
      lastScrollTimeRef.current = currentTime;
      resetIdleTimer();
    };
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    resetIdleTimer();
    return () => {
      clearInterval(activeTimeInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
      window.removeEventListener('scroll', handleScroll);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (mouseLeaveTimerRef.current) clearTimeout(mouseLeaveTimerRef.current);
    };
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const payload = {
        email,
        source: 'exit_intent',
        timestamp: new Date().toISOString(),
        utm_source: urlParams.get('utm_source') || '',
        utm_medium: urlParams.get('utm_medium') || '',
        utm_campaign: urlParams.get('utm_campaign') || '',
        click_id: urlParams.get('click_id') || ''
      };
      console.log('Exit Intent Payload:', payload);
      const FORM_ENDPOINT = 'https://hooks.zapier.com/hooks/catch/PLACEHOLDER/PLACEHOLDER/';
      if (FORM_ENDPOINT.includes('PLACEHOLDER')) {
        await new Promise(resolve => setTimeout(resolve, 1500));
      } else {
        const response = await fetch(FORM_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Network response was not ok');
      }
      trackLead('exit_intent', {
        email_only: true
      });
      setIsSubmitting(false);
      setIsSuccess(true);
      setTimeout(() => {
        setIsOpen(false);
      }, 2000);
    } catch (err) {
      setIsSubmitting(false);
      setIsOpen(false);
    }
  };
  return <AnimatePresence data-id="element-87">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-88">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setIsOpen(false)} className="absolute inset-0 bg-navy/60 backdrop-blur-sm" data-id="element-89" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" data-id="element-90">
            <button onClick={() => setIsOpen(false)} className="absolute top-4 right-4 text-slate hover:text-charcoal transition-colors z-10" aria-label="Close modal" data-id="element-91">
              <X className="w-6 h-6" data-id="element-92" />
            </button>

            {isSuccess ? <div className="p-8 text-center flex flex-col items-center justify-center h-full min-h-[300px]" data-id="element-93">
                <div className="w-16 h-16 bg-emerald/10 rounded-full flex items-center justify-center mb-6" data-id="element-94">
                  <ShieldCheck className="w-8 h-8 text-emerald" data-id="element-95" />
                </div>
                <h3 className="text-2xl font-bold text-charcoal mb-2" data-id="element-96">
                  Documentation Dispatched
                </h3>
                <p className="text-slate leading-[1.6]" data-id="element-97">
                  Check your inbox shortly.
                </p>
              </div> : <div className="p-6 sm:p-8 text-center" data-id="element-98">
                <h3 className="text-2xl font-bold text-charcoal mb-2" data-id="element-99">
                  Review the reporting framework before exiting
                </h3>
                <p className="text-slate font-medium text-sm leading-[1.6] mb-6" data-id="element-100">
                  Access the documentation detailing how bureau inconsistencies
                  are identified and disputed under federal law.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4" data-id="element-101">
                  <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-emerald focus:ring-2 focus:ring-emerald/20 outline-none transition-all text-center" placeholder="Enter your email address" data-id="element-102" />

                  <button type="submit" disabled={isSubmitting} className="w-full bg-emerald hover:bg-emerald-500 text-white font-bold py-4 px-6 rounded-lg shadow-cta hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-103">
                    {isSubmitting ? 'Processing...' : 'Access Documentation'}
                  </button>

                  <button type="button" onClick={() => setIsOpen(false)} className="text-sm text-slate-400 hover:text-slate-600 font-medium mt-4 transition-colors" data-id="element-104">
                    Close window
                  </button>
                </form>
              </div>}
          </motion.div>
        </div>}
    </AnimatePresence>;
}